import { useCallback, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { format } from 'date-fns'
import { Loader2, Minus, Plus } from 'lucide-react'

import { Button } from '@/components/shadcn/button'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcn/table'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'

import { useTileFilters } from '@/catalog-apps/hooks/useTileFilters'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import { getComponentHeights } from '../utils'
import { useSubmitForm } from '../FormComponent/useSubmitForm'
import { CompactViewToggleV2 } from '../DataTableV2/CompactViewToggleV2'
import { TableActionsV2 } from '../DataTableV2/components/TableActionsV2'

import { TriangleGradingLegend } from './TriangleGradingLegend'
import { TriangleCell, TriangleDataSchema, TriangleSubmitPayload, TriangleUISchema } from './types'
import { useIsDarkMode } from './useIsDarkMode'
import {
  buildExclusionSubmitPayload,
  buildTriangleGrid,
  cellKey,
  downloadCsv,
  exportFilenamePrefix,
  exportTriangleCsv,
  formatTriangleValue,
  getCellExclusionDisplay,
  getCellGradingBackground,
  getEffectiveExcluded,
  hasExclusionOverrides,
  isFullyExcludedAxis,
} from './triangleUtils'
import { IconDocumentExportOutline } from '@icons/IconDocumentExportOutline'
import { TooltipButton } from '@/components/ui/TooltipButton'

/** Vertical-only resize — avoids horizontal reflow from font/padding/min-width changes. */
const TRIANGLE_DENSITY_TRANSITION =
  'transition-[height,min-height,padding-top,padding-bottom,top] duration-200 ease-in-out motion-reduce:transition-none'

/** Shared typography and horizontal metrics so column widths stay stable when toggling compact. */
const TRIANGLE_CELL_TEXT = 'text-xs tabular-nums'
const TRIANGLE_DATA_COL_MIN_W = 'min-w-[4.5rem]'
const TRIANGLE_CELL_PX = 'px-2'

function coerceBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true') return true
    if (normalized === 'false') return false
  }
  return fallback
}

function TriangleCellTooltip({ cell, children }: { cell: TriangleCell; children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="left" className="max-w-xs">
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
          {cell.tooltip.map((entry) => (
            <div key={`${entry.label}-${entry.value}`} className="contents">
              <dt className="text-background/70">{entry.label}</dt>
              <dd className="font-medium">: {entry.value}</dd>
            </div>
          ))}
        </dl>
      </TooltipContent>
    </Tooltip>
  )
}

export function TriangleContent({
  uiSchema,
  dataSchema,
  tileFilters,
}: {
  uiSchema: TriangleUISchema
  dataSchema: TriangleDataSchema
  tileFilters?: React.ReactNode
}) {
  const {
    title,
    description,
    emphasizeHeader,
    rowLabel,
    colLabel,
    valueFormat,
    selectable = false,
    submitHandlerId,
    submitButtonLabel = 'Submit',
    canExport = false,
    exportFileName,
    gradingMethod = null,
    showLegend: showLegendProp,
    defaultCompactMode: defaultCompactModeProp,
    triangleActions = [],
  } = uiSchema

  const showGradingLegend = coerceBoolean(showLegendProp, false) && gradingMethod != null
  const isDarkMode = useIsDarkMode()
  const defaultCompactMode = coerceBoolean(defaultCompactModeProp, false)
  const [compactMode, setCompactMode] = useState(() => defaultCompactMode)

  useEffect(() => {
    setCompactMode(defaultCompactMode)
  }, [defaultCompactMode])

  const { cells } = dataSchema
  const grid = useMemo(() => buildTriangleGrid(cells), [cells])
  const { originDates, developmentPeriods, cellByKey } = grid

  const [exclusionOverrides, setExclusionOverrides] = useState<Map<string, boolean>>(() => new Map())
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false)

  const { tileFilterSelections } = useTileFilters()
  const { mutate: submitExclusions, isPending: isSubmitting } = useSubmitForm(
    submitHandlerId ?? '',
    tileFilterSelections,
    'triangle-submit-exclusions'
  )

  const isCellExcluded = useCallback(
    (cell: TriangleCell) => getEffectiveExcluded(cell, exclusionOverrides),
    [exclusionOverrides]
  )

  const hasLocalChanges = useMemo(
    () => hasExclusionOverrides(exclusionOverrides, cellByKey),
    [cellByKey, exclusionOverrides]
  )

  const fullyExcludedRows = useMemo(
    () => isFullyExcludedAxis(originDates, developmentPeriods, cellByKey, 'row', isCellExcluded),
    [originDates, developmentPeriods, cellByKey, isCellExcluded]
  )

  const fullyExcludedColumns = useMemo(
    () => isFullyExcludedAxis(developmentPeriods, originDates, cellByKey, 'column', isCellExcluded),
    [developmentPeriods, originDates, cellByKey, isCellExcluded]
  )

  const isCompact = compactMode
  const hasColLabel = !!colLabel
  const hasAxisLabelRow = true

  const dataRowHeightClass = isCompact ? 'h-8 min-h-8' : 'h-10 min-h-10'
  const axisLabelRowHeightClass = isCompact ? 'h-7 min-h-7' : 'h-8 min-h-8'
  /** Offset for the period-header row when the axis-label row sits above it (animates with axis row height). */
  const periodHeaderStickyTop = hasAxisLabelRow ? (isCompact ? 'top-7' : 'top-8') : 'top-0'

  const stickyRowColumnClass = cn(
    TRIANGLE_DENSITY_TRANSITION,
    TRIANGLE_CELL_TEXT,
    TRIANGLE_CELL_PX,
    'w-0 whitespace-nowrap bg-background',
    dataRowHeightClass,
    isCompact ? 'py-1' : 'py-2'
  )
  const stickyCornerClass = cn(
    stickyRowColumnClass,
    'sticky left-0 z-30 border-b border-x shadow-[2px_2px_4px_-2px_rgba(0,0,0,0.12)] text-sm font-bold bg-offwhite',
    periodHeaderStickyTop
  )
  const stickyColLabelCornerClass = cn(
    TRIANGLE_DENSITY_TRANSITION,
    TRIANGLE_CELL_PX,
    'w-0 whitespace-nowrap bg-offwhite',
    axisLabelRowHeightClass,
    'sticky left-0 top-0 z-30 border-x border-t shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]'
  )
  const stickyColHeaderClass = cn(
    TRIANGLE_DENSITY_TRANSITION,
    TRIANGLE_CELL_TEXT,
    TRIANGLE_DATA_COL_MIN_W,
    TRIANGLE_CELL_PX,
    'sticky z-20 border-b bg-background text-center shadow-[0_2px_4px_-2px_rgba(0,0,0,0.1)]',
    dataRowHeightClass,
    periodHeaderStickyTop,
    isCompact ? 'py-1' : 'py-2'
  )
  const stickyColLabelClass = cn(
    TRIANGLE_DENSITY_TRANSITION,
    TRIANGLE_CELL_PX,
    'sticky top-0 z-20 border-y bg-offwhite shadow-[0_2px_4px_-2px_rgba(0,0,0,0.08)]',
    axisLabelRowHeightClass,
    isCompact ? 'py-1' : 'py-1.5'
  )
  const stickyRowHeaderClass = cn(
    stickyRowColumnClass,
    'sticky left-0 z-10 border-x font-medium shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]'
  )
  const dataCellClass = cn(
    TRIANGLE_DENSITY_TRANSITION,
    TRIANGLE_DATA_COL_MIN_W,
    'p-0.25 rounded-lg overflow-hidden',
    dataRowHeightClass
  )
  const nullDataCellClass = cn(dataCellClass, 'bg-muted/40')

  const handleCellClick = (cell: TriangleCell) => {
    if (!selectable) return
    const key = cellKey(cell.originDate, cell.developmentPeriod)
    setExclusionOverrides((prev) => {
      const next = new Map(prev)
      const nextExcluded = !getEffectiveExcluded(cell, prev)
      if (nextExcluded === cell.excluded) {
        next.delete(key)
      } else {
        next.set(key, nextExcluded)
      }
      return next
    })
  }

  const handleReset = () => {
    setExclusionOverrides(new Map())
    setIsResetDialogOpen(false)
  }

  const handleSubmit = () => {
    if (!submitHandlerId || isSubmitting || !hasLocalChanges) return
    const payload: TriangleSubmitPayload = buildExclusionSubmitPayload(cells, exclusionOverrides)
    submitExclusions(payload as unknown as Record<string, unknown>, {
      onSuccess: (response) => {
        if (response.uiSchema?.success === false) return
        setExclusionOverrides(new Map())
      },
    })
  }

  const handleExport = () => {
    const csv = exportTriangleCsv({
      originDates,
      developmentPeriods,
      cellByKey,
      isExcluded: isCellExcluded,
      valueFormat,
    })
    const timestamp = format(new Date(), 'yyyy-MM-dd_HH-mm-ss')
    const prefix = exportFilenamePrefix(exportFileName, title)
    downloadCsv(csv, `${prefix} - ${timestamp}.csv`)
  }

  const hasHeader = !!title || !!tileFilters
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)

  if (cells.length === 0) {
    return (
      <div className="flex h-full flex-col">
        {hasHeader && (
          <ComponentHeader emphasize={emphasizeHeader}>
            <ComponentTitle title={title} description={description} />
            {tileFilters && <ComponentActionsContainer>{tileFilters}</ComponentActionsContainer>}
          </ComponentHeader>
        )}
        <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">No data available</div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      {hasHeader && (
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          <ComponentActionsContainer>
            <div className="flex items-center gap-0">
              <TableActionsV2 actions={triangleActions} />
              {canExport && (
                <TooltipButton tooltip="Export CSV">
                  <Button size="icon" variant="ghost" className="size-8 rounded-full" onClick={handleExport}>
                    <IconDocumentExportOutline className="size-4" />
                  </Button>
                </TooltipButton>
              )}
            </div>
            {tileFilters}
            {selectable && (hasLocalChanges || isSubmitting) && (
              <>
                {!isSubmitting && (
                  <ConfirmDialog
                    trigger={
                      <Button variant="outline" size="sm">
                        Reset
                      </Button>
                    }
                    isOpen={isResetDialogOpen}
                    onOpenChange={setIsResetDialogOpen}
                    title="Reset exclusions"
                    description="Reset all exclusions to their original state?"
                    onConfirm={handleReset}
                  />
                )}
                <Button
                  size="sm"
                  disabled={!submitHandlerId || isSubmitting}
                  aria-busy={isSubmitting}
                  onClick={handleSubmit}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
                      Submitting…
                    </>
                  ) : (
                    submitButtonLabel
                  )}
                </Button>
              </>
            )}
          </ComponentActionsContainer>
        </ComponentHeader>
      )}

      <div className={clsx('flex min-h-0 flex-1 flex-col')} style={{ height: contentHeight }}>
        <div className="min-h-0 flex-1 overflow-auto">
          <div className="pb-4 pt-5">
            <table className="w-max min-w-full border-separate border-spacing-0 border-b border-r text-sm">
              <TableHeader>
                {hasAxisLabelRow && (
                  <TableRow className="hover:bg-transparent">
                    <TableHead className={stickyColLabelCornerClass} aria-hidden />
                    <TableHead colSpan={developmentPeriods.length} className={stickyColLabelClass}>
                      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 px-1">
                        <span aria-hidden />
                        {hasColLabel ? (
                          <span className="text-center font-bold text-muted-foreground">{colLabel}</span>
                        ) : (
                          <span aria-hidden />
                        )}
                        <CompactViewToggleV2 compactMode={compactMode} setCompactMode={setCompactMode} inline />
                      </div>
                    </TableHead>
                  </TableRow>
                )}
                <TableRow className="hover:bg-transparent">
                  <TableHead className={stickyCornerClass}>{rowLabel ?? ''}</TableHead>
                  {developmentPeriods.map((period) => (
                    <TableHead
                      key={period}
                      className={cn(
                        stickyColHeaderClass,
                        fullyExcludedColumns[period] && 'text-muted-foreground line-through'
                      )}
                    >
                      {period}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {originDates.map((originDate) => (
                  <TableRow key={originDate} className="hover:bg-transparent">
                    <TableCell
                      className={cn(
                        stickyRowHeaderClass,
                        fullyExcludedRows[originDate] && 'text-muted-foreground line-through'
                      )}
                    >
                      {originDate}
                    </TableCell>
                    {developmentPeriods.map((developmentPeriod) => {
                      const cell = cellByKey.get(cellKey(originDate, developmentPeriod))
                      if (!cell) {
                        return <TableCell key={developmentPeriod} className={nullDataCellClass} />
                      }

                      const exclusionDisplay = getCellExclusionDisplay(cell, exclusionOverrides)
                      const serverExcluded = cell.excluded
                      const gradingBackground = getCellGradingBackground(
                        gradingMethod,
                        serverExcluded ? null : cell.gradingValue,
                        isDarkMode
                      )
                      const isPendingChange =
                        exclusionDisplay === 'pending-exclude' || exclusionDisplay === 'pending-include'

                      const cellContent = (
                        <button
                          type="button"
                          disabled={!selectable}
                          onClick={() => handleCellClick(cell)}
                          aria-label={
                            exclusionDisplay === 'pending-exclude'
                              ? 'Marked to exclude on submit'
                              : exclusionDisplay === 'pending-include'
                                ? 'Marked to include on submit'
                                : exclusionDisplay === 'unchanged-excluded'
                                  ? 'Excluded'
                                  : undefined
                          }
                          className={cn(
                            TRIANGLE_DENSITY_TRANSITION,
                            TRIANGLE_CELL_TEXT,
                            TRIANGLE_CELL_PX,
                            'relative flex h-full w-full items-center justify-center transition-colors',

                            isCompact ? 'py-1' : 'py-1.5',
                            selectable && 'cursor-pointer hover:ring-2 hover:ring-ring/40',
                            serverExcluded && 'bg-foreground/80 text-background'
                          )}
                          style={
                            !serverExcluded && gradingBackground ? { backgroundColor: gradingBackground } : undefined
                          }
                        >
                          {formatTriangleValue(cell.value, valueFormat)}
                          {isPendingChange && (
                            <span
                              className="pointer-events-none absolute right-0.5 top-0.5 flex size-4 items-center justify-center rounded-full bg-background/95 shadow-sm ring-1 ring-border"
                              aria-hidden
                            >
                              {exclusionDisplay === 'pending-exclude' ? (
                                <Minus className="size-2.5 text-destructive" strokeWidth={2.5} />
                              ) : (
                                <Plus className="size-2.5 text-emerald-600" strokeWidth={2.5} />
                              )}
                            </span>
                          )}
                        </button>
                      )

                      return (
                        <TableCell key={developmentPeriod} className={dataCellClass}>
                          <TriangleCellTooltip cell={cell}>{cellContent}</TriangleCellTooltip>
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </table>
          </div>
        </div>

        {showGradingLegend && gradingMethod && (
          // <div className="z-20 shrink-0 border-t bg-background/95 px-4 py-2.5 shadow-[0_-4px_12px_-4px_rgba(0,0,0,0.1)] backdrop-blur-sm supports-[backdrop-filter]:bg-background/80">
          <div className="z-20 shrink-0 rounded-lg bg-offwhite px-4 py-2.5 shadow backdrop-blur-sm">
            <TriangleGradingLegend gradingMethod={gradingMethod} />
          </div>
        )}
      </div>
    </div>
  )
}
